const matchDataViewAt = (
    query: string | any[],
    reader: DataView,
    offset: number,
) => {
    // Check if each byte in query matches the corresponding byte from the current offset
    for (let i = 0, il = query.length; i < il; i++) {
        if (query[i] !== reader.getUint8(offset + i)) {
            return false
        }
    }
    return true
}

const looksLikeAsciiSTL = (data: any) => {
    const reader = new DataView(data)
    const face_size = (32 / 8) * 3 + (32 / 8) * 3 * 3 + 16 / 8
    const n_faces = reader.getUint32(80, true)
    const expect = 80 + 32 / 8 + n_faces * face_size

    if (expect === reader.byteLength) {
        return false
    } // An ASCII STL data must begin with 'solid ' as the first six bytes.
    // However, ASCII STLs lacking the SPACE after the 'd' are known to be
    // plentiful.  So, check the first 5 bytes for 'solid'.
    // Several encodings, such as UTF-8, precede the text with up to 5 bytes:
    // https://en.wikipedia.org/wiki/Byte_order_mark#Byte_order_marks_by_encoding
    // Search for "solid" to start anywhere after those prefixes.
    // US-ASCII ordinal values for 's', 'o', 'l', 'i', 'd'
    const solid = [115, 111, 108, 105, 100]

    for (let off = 0; off < 5; off++) {
        // If "solid" text is matched to the current offset, declare it to be an ASCII STL.
        if (matchDataViewAt(solid, reader, off)) {
            return true
        }
    } // Couldn't find "solid" text at the beginning; it is binary STL.

    return false
}

const readAsciiSTL = (f: any, data: any) => {
    data = new TextDecoder().decode(new Uint8Array(data))
    const patternSolid = /solid([\s\S]*?)endsolid/g
    const patternFace = /facet([\s\S]*?)endfacet/g
    let result
    const patternFloat = /[\s]+([+-]?(?:\d*)(?:\.\d*)?(?:[eE][+-]?\d+)?)/.source
    const patternVertex = new RegExp(
        `vertex${patternFloat}${patternFloat}${patternFloat}`,
        'g',
    )
    let minX = 1e10,
        minY = 1e10,
        minZ = 1e10,
        maxX = -1e10,
        maxY = -1e10,
        maxZ = -1e10

    while ((result = patternSolid.exec(data)) !== null) {
        const solid = result[0]

        while ((result = patternFace.exec(solid)) !== null) {
            const text = result[0]

            while ((result = patternVertex.exec(text)) !== null) {
                //result[0] is the full line, and the other indices are the number matches
                minX = Math.min(minX, parseFloat(result[1]))
                minY = Math.min(minY, parseFloat(result[2]))
                minZ = Math.min(minZ, parseFloat(result[3]))
                maxX = Math.max(maxX, parseFloat(result[1]))
                maxY = Math.max(maxY, parseFloat(result[2]))
                maxZ = Math.max(maxZ, parseFloat(result[3]))
            }
        }
    }
    const dimensions = {
        name: f.name,
        dimensions: `${(maxX - minX).toFixed(3)} x ${(maxY - minY).toFixed(
            3,
        )} x ${(maxZ - minZ).toFixed(3)}`,
    }

    return dimensions
}

const readBinarySTL = async (f: any, data: any) => {
    const reader = new DataView(data)
    const faces = reader.getUint32(80, true)
    const dataOffset = 84
    const faceLength = 12 * 4 + 2
    let minX = 1e10,
        minY = 1e10,
        minZ = 1e10,
        maxX = -1e10,
        maxY = -1e10,
        maxZ = -1e10

    for (let face = 0; face < faces; face++) {
        const start = dataOffset + face * faceLength

        for (let i = 1; i <= 3; i++) {
            const vertexstart = start + i * 12
            const x = reader.getFloat32(vertexstart, true)
            const y = reader.getFloat32(vertexstart + 4, true)
            const z = reader.getFloat32(vertexstart + 8, true)

            minX = Math.min(minX, x)
            minY = Math.min(minY, y)
            minZ = Math.min(minZ, z)
            maxX = Math.max(maxX, x)
            maxY = Math.max(maxY, y)
            maxZ = Math.max(maxZ, z)
        }
    }

    const dimensions = {
        name: f.name,
        dimensions: `${(maxX - minX).toFixed(3)} x ${(maxY - minY).toFixed(
            3,
        )} x ${(maxZ - minZ).toFixed(3)}`,
    }

    return dimensions
}

const readSTL = (f: any) => {
    return new Promise(resolve => {
        const reader = new FileReader()

        reader.addEventListener(
            'loadend',
            async () => {
                const looksLikeAscii = await looksLikeAsciiSTL(reader.result)

                if (looksLikeAscii) {
                    resolve(readAsciiSTL(f, reader.result))
                } else {
                    resolve(readBinarySTL(f, reader.result))
                }
            },
            false,
        )
        reader.readAsArrayBuffer(f)
    })
}

const readFileData = (f: any) => {
    const ext = f.name.split('.').pop().toLowerCase()

    if (ext === 'stl') {
        return readSTL(f)
    }
}

export const getDimensionsOnUploadFile = (files: any[]) => {
    const output: any = []

    for (let i = 0, f; (f = files[i]); i++) {
        output.push(readFileData(f))
    }

    return Promise.all(output)
}

export const convertMilimetersToInches = (dimensions: string) => {
    const values = dimensions.split('x')

    return values.map(value => (Number(value) / 25.4).toFixed(3))
}

export const convertInchesToMilimeters = (dimensions: string) => {
    const values = dimensions.split('x')

    return values.map(value => (Number(value) * 25.4).toFixed(3))
}
